import { Component, Inject, inject, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MasterService } from "../../../../serivces/master.service";
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from "@angular/material/dialog";
import { SVG_ICONS } from "../../../../models/icons";

import { Cls_PackageData } from "../../../../models/master";
import { FormsModule } from "@angular/forms";
import { ToastrService } from "@frontend/app/serivces/toastr.service";
import { MultiSelectDropdownComponent } from "@frontend/app/components/multi-select-dropdown/multi-select-dropdown.component";
import { AsteriskMessageDirective } from "@frontend/app/directives/asterikmessage.directive";

@Component({
  selector: "app-add-plan",
  imports: [CommonModule, FormsModule, MultiSelectDropdownComponent, AsteriskMessageDirective, MatDialogModule],
  templateUrl: "./add-plan.component.html",
  styleUrl: "./add-plan.component.scss",
})
export class AddPlanComponent implements OnInit {
  private masterService = inject(MasterService);
  private toastService = inject(ToastrService);

  constructor(@Inject(MAT_DIALOG_DATA) public data: any, private dialogRef: MatDialogRef<AddPlanComponent>) {}

  ngOnInit() {
    if (this.data?.packageId) {
      this.PackageID = this.data.packageId;
      this.getPackageDetailsByID(this.PackageID);
    }
    this.getCompanyMaster();
    if (!this.packageDetails || !this.packageDetails.PackageID) {
      this.packageDetails = {
        ...this.packageDetails,
        IsActive: true,
        IsUpgradable: true,
        IsRenewable: true,
        IsTransferable: true,
      };
    }
    if (this.packageDetails?.FinalAmount) {
      // If you want to show Final Amount by default when editing:
      this.enteredAmount = this.packageDetails.FinalAmount;
      this.isGSTInclusive = true;
    }
    this.getRegionList();
    this.getAreaList();
    this.getCenterList();
  }

  //#region variables
  PackageID: any;
  packageDetails = new Cls_PackageData();
  readonly SVG_ICONS = SVG_ICONS;
  companyList: any;
  selectedTab: "Global Package" | "Local Package(Area Wise)" = "Global Package";
  areaList: any;
  regionList: any;
  SGST = 0;
  CGST = 0;
  isLocal = false;
  ErrorMsg: string[] = [];
  centerList: any;
  allCenterList: any[] = [];
  enteredAmount = 0;
  isGSTInclusive = false;
  areaCenterMap: { [areaId: number]: number[] } = {};

  minAmountRs: number | null = null;
  minAmountPercent: number | null = null;

  //#endregion

  onTabChange(tab: "Global Package" | "Local Package(Area Wise)") {
    if (this.selectedTab === tab) return;
    this.selectedTab = tab;
  }

  // Prevent minus key and minus key on numpad
  preventMinus(event: KeyboardEvent) {
    if (event.code === "Minus" || event.code === "NumpadSubtract") {
      event.preventDefault();
    }
  }

  onMinAmountRsChange() {
    const total = this.packageDetails.FinalAmount || 0;
    const val = this.minAmountRs || 0;
    if (total > 0) {
      this.minAmountPercent = parseFloat(((val / total) * 100).toFixed(2));
    } else {
      this.minAmountPercent = 0;
    }
  }

  onMinAmountPercentChange() {
    const total = this.packageDetails.FinalAmount || 0;
    const val = this.minAmountPercent || 0;
    if (total > 0) {
      this.minAmountRs = parseFloat(((total * val) / 100).toFixed(2));
    } else {
      this.minAmountRs = 0;
    }
  }

  // Handle renewal amount changes
  onRenewAmountChange(value: number) {
    this.validateAmount("RenewAmount", value);
    this.packageDetails.RenewAmount = value;
  }

  // Validate amount fields to prevent negative numbers
  validateAmount(field: "Amount" | "RenewAmount", value: number) {
    if (value < 0) {
      // Reset to 0 if negative
      this.packageDetails[field] = 0;
      this.toastService.warning("Negative values are not allowed");
    }
  }

  // Handle start date change
  onStartDateChange(selectedDate: string) {
    if (this.packageDetails.EndTime && new Date(selectedDate) > new Date(this.packageDetails.EndTime)) {
      // If new start date is after end date, update end date to be same as start date
      this.packageDetails.EndTime = selectedDate;
      this.toastService.warning("End date has been adjusted to match the start date");
    }
  }

  // Handle end date change
  onEndDateChange(selectedDate: string) {
    if (this.packageDetails.StartTime && new Date(selectedDate) < new Date(this.packageDetails.StartTime)) {
      // If new end date is before start date, update start date to be same as end date
      this.packageDetails.StartTime = selectedDate;
      this.toastService.warning("Start date has been adjusted to match the end date");
    }
  }

  // Validate that end date is not before start date
  private validateDates(): boolean {
    if (this.packageDetails.StartTime && this.packageDetails.EndTime) {
      const startDate = new Date(this.packageDetails.StartTime);
      const endDate = new Date(this.packageDetails.EndTime);

      if (endDate < startDate) {
        this.toastService.error("End date cannot be before start date");
        return false;
      }
    }
    return true;
  }

  validateForm(): boolean {
    this.ErrorMsg = [];

    const requiredFields: {
      key: keyof Cls_PackageData;
      label: string;
      type?: "string" | "date" | "number";
    }[] = [
      { key: "PackageName", label: "Package Name", type: "string" },
      { key: "Amount", label: "Amount", type: "number" },
      { key: "StartTime", label: "Start Date", type: "date" },
      { key: "EndTime", label: "End Date", type: "date" },
      { key: "Duration", label: "Duration (in Months)", type: "number" },
      // { key: "DurationInDays", label: "Total Days", type: "number" },
      // { key: "RenewAmount", label: "Renew Amount", type: "number" },
    ];

    // ---------- REQUIRED FIELD VALIDATION ----------
    for (const field of requiredFields) {
      const value = this.packageDetails[field.key];

      if (value == null || (field.type === "string" && !String(value).trim()) || (field.type === "number" && Number(value) < 0)) {
        this.ErrorMsg.push(`${field.label} is required`);
        continue;
      }
    }

    // ---------- DATE COMPARISON VALIDATION ----------
    if (this.packageDetails.StartTime && this.packageDetails.EndTime) {
      const start = new Date(this.packageDetails.StartTime);
      const end = new Date(this.packageDetails.EndTime);

      if (end < start) {
        this.ErrorMsg.push("End date cannot be before start date");
      }
    }

    const isValid = this.ErrorMsg.length === 0;
    if (!isValid) {
      this.toastService.error("Please fill in all required fields", "Validation Error");
    }
    return isValid;
  }

  addPackage() {
    this.ErrorMsg = [];
    if (!this.validateForm() || !this.validateDates()) {
      return;
    }
    const tempModal = new Cls_PackageData();
    tempModal.PackageID = this.packageDetails.PackageID || 0;
    tempModal.PackageAction = this.isLocal ? "Local" : "National";

    // Construct PackageAccessList
    tempModal.PackageAccessList = [];
    if (this.isLocal) {
      if (this.packageDetails.CenterID && this.packageDetails.CenterID.length > 0) {
        this.packageDetails.CenterID.forEach((centerId: number) => {
          const center = this.allCenterList.find((c: any) => c.CenterID === centerId);
          if (center) {
            tempModal.PackageAccessList?.push({
              ID: center.AreaID,
              AreaID: center.AreaID,
              CenterID: center.CenterID,
            });
          }
        });
      }
    } else {
      tempModal.PackageAccessList.push({
        ID: 0,
        AreaID: 0,
        CenterID: 0,
      });
    }

    // tempModal.ID = this.isLocal ? (this.packageDetails.AreaIDs ? this.packageDetails.AreaIDs : []) : [0];
    // tempModal.CenterID = this.isLocal ? (this.packageDetails.CenterID ? this.packageDetails.CenterID : []) : [0];
    tempModal.TrainerID = this.packageDetails.TrainerID ? this.packageDetails.TrainerID : 0;
    tempModal.PackageName = this.packageDetails.PackageName;
    tempModal.MembershipID = 1;
    tempModal.Duration = this.packageDetails.Duration;
    // tempModal.TotalDays = this.packageDetails.TotalDays;

    tempModal.Amount = this.packageDetails.Amount;
    // tempModal.TrainerPercentage = this.packageDetails.TrainerPercentage; // Not in new JSON, but in old model
    tempModal.CGST = this.packageDetails.CGST;
    tempModal.SGST = this.packageDetails.SGST;
    tempModal.FinalAmount = this.packageDetails.FinalAmount;
    tempModal.RenewAmount = this.packageDetails.RenewAmount;
    tempModal.MinimumAmount = this.minAmountPercent || 0;
    tempModal.IsPT = this.packageDetails.IsPT;
    tempModal.StartTime = this.packageDetails.StartTime;
    tempModal.EndTime = this.packageDetails.EndTime;
    tempModal.Status = this.packageDetails.Status;
    tempModal.Description = this.packageDetails.Description;
    tempModal.IsActive = this.packageDetails.IsActive ?? false;
    tempModal.IsRenewable = this.packageDetails.IsRenewable ?? false;
    tempModal.IsUpgradable = this.packageDetails.IsUpgradable ?? false;
    tempModal.IsTransferable = this.packageDetails.IsTransferable ?? false;

    if (this.packageDetails.PackageID && this.packageDetails.PackageID > 0) {
      this.masterService.updatePackage(tempModal, this.packageDetails.PackageID).subscribe((res: any) => {
        this.toastService.success("Package Updated Successfully");
        this.dialogRef.close(res);
      });
    } else {
      this.masterService.addPackage(tempModal).subscribe((res: any) => {
        this.toastService.success("Package Added Successfully");
        this.dialogRef.close(res);
      });
    }
  }

  calculateAmounts() {
    const value = Number(this.enteredAmount) || 0;
    const cgstPercent = this.companyList?.CGST || 0;
    const sgstPercent = this.companyList?.SGST || 0;
    const totalTaxPercent = cgstPercent + sgstPercent;

    if (this.isGSTInclusive) {
      // --- MODE: WITH GST ---
      // The entered value is the FINAL TOTAL.
      // We calculate BACKWARDS to find the Base.

      this.packageDetails.FinalAmount = value;

      // Formula: Base = Total / (1 + Tax%)
      const baseAmount = +(value / (1 + totalTaxPercent / 100)).toFixed(2);

      this.packageDetails.Amount = baseAmount;
      this.packageDetails.CGST = +(baseAmount * (cgstPercent / 100)).toFixed(2);
      this.packageDetails.SGST = +(baseAmount * (sgstPercent / 100)).toFixed(2);
    } else {
      // --- MODE: WITHOUT GST ---
      // The entered value is the BASE AMOUNT.
      // We calculate FORWARDS to find the Total.

      this.packageDetails.Amount = value;

      const cgst = +(value * (cgstPercent / 100)).toFixed(2);
      const sgst = +(value * (sgstPercent / 100)).toFixed(2);

      this.packageDetails.CGST = cgst;
      this.packageDetails.SGST = sgst;
      this.packageDetails.FinalAmount = +(value + cgst + sgst).toFixed(2);
    }

    // Auto-fill Renewal Amount with the Total
    this.packageDetails.RenewAmount = this.packageDetails.FinalAmount;

    // Recalculate Min amounts to keep them consistent
    if (this.minAmountPercent && this.minAmountPercent > 0) {
      this.onMinAmountPercentChange();
    } else if (this.minAmountRs && this.minAmountRs > 0) {
      this.onMinAmountRsChange();
    }
  }

  // Triggered when checkbox is toggled
  onTaxModeChange() {
    // Recalculate purely to update the UI numbers immediately
    this.calculateAmounts();
  }

  calculateFromTotal() {
    const total = Number(this.packageDetails.FinalAmount) || 0;

    this.packageDetails.RenewAmount = total;

    const cgstPercent = this.companyList?.CGST || 0;
    const sgstPercent = this.companyList?.SGST || 0;

    const totalTaxPercent = cgstPercent + sgstPercent;

    // Reverse calculate base amount
    const baseAmount = +(total / (1 + totalTaxPercent / 100)).toFixed(2);

    const cgst = +(baseAmount * (cgstPercent / 100)).toFixed(2);
    const sgst = +(baseAmount * (sgstPercent / 100)).toFixed(2);

    this.packageDetails.Amount = baseAmount;
    this.packageDetails.CGST = cgst;
    this.packageDetails.SGST = sgst;
  }

  calculateTotalDays() {
    if (this.packageDetails.Duration) {
      this.packageDetails.DurationInDays = this.packageDetails.Duration * 30;
    } else {
      this.packageDetails.DurationInDays = 0;
    }
  }

  // addPackage() {
  //   const tempModal = new Cls_PackageData();
  //   // tempModal.PackageID = this.packageDetails.PackageID;
  //   tempModal.TrainerID = this.packageDetails.TrainerID ? this.packageDetails.TrainerID : 0;
  //   tempModal.PackageName = this.packageDetails.PackageName;
  //   tempModal.MembershipID = 1;
  //   tempModal.CenterIDs = Array.isArray(this.packageDetails.CenterIDs) ? this.packageDetails.CenterIDs : [];
  //   tempModal.RegionIDs = Array.isArray(this.packageDetails.RegionIDs) ? this.packageDetails.RegionIDs : [];
  //   tempModal.Amount = this.packageDetails.Amount;
  //   // tempModal.TrainerPercentage = this.packageDetails.TrainerPercentage;
  //   tempModal.CGST = this.packageDetails.CGST;
  //   tempModal.SGST = this.packageDetails.SGST;
  //   tempModal.FinalAmount = this.packageDetails.FinalAmount;
  //   tempModal.RenewAmount = this.packageDetails.RenewAmount;
  //   tempModal.IsPT = this.packageDetails.IsPT;
  //   tempModal.StartTime = this.packageDetails.StartTime;
  //   tempModal.EndTime = this.packageDetails.EndTime;
  //   tempModal.Status = this.packageDetails.Status;
  //   tempModal.Description = this.packageDetails.Description;
  //   if (!this.packageDetails.PackageID || this.packageDetails.PackageID === 0) {
  //     // Creating new package → set defaults
  //     tempModal.IsActive = true;
  //     tempModal.IsRenewable = true;
  //     tempModal.IsUpgradable = true;
  //     tempModal.IsTransferable = true;
  //   } else {
  //     // Editing → keep values from packageDetails
  //     tempModal.IsActive = this.packageDetails.IsActive;
  //     tempModal.IsRenewable = this.packageDetails.IsRenewable;
  //     tempModal.IsUpgradable = this.packageDetails.IsUpgradable;
  //     tempModal.IsTransferable = this.packageDetails.IsTransferable;
  //   }

  //   if (this.packageDetails.PackageID && this.packageDetails.PackageID > 0) {
  //     this.masterService.updatePackage(tempModal, this.packageDetails.PackageID).subscribe((res: any) => {
  //       this.toastService.success("Package Updated Successfully");
  //       this.dialogRef.close(res);
  //     });
  //   } else {
  //     this.masterService.addPackage(tempModal).subscribe((res: any) => {
  //       this.toastService.success("Package Added Successfully");
  //       this.dialogRef.close(res);
  //     });
  //   }
  // }

  calculateFinalAmount() {
    const amount = Number(this.packageDetails.FinalAmount) || 0;

    // Auto-fill renewal amount
    this.packageDetails.RenewAmount = amount;

    // CGST / SGST calculation
    const cgst = +(amount * (this.companyList.CGST / 100 || 0)).toFixed(2);
    const sgst = +(amount * (this.companyList.SGST / 100 || 0)).toFixed(2);
    const final = +(amount + cgst + sgst).toFixed(2);

    this.packageDetails.CGST = cgst;
    this.packageDetails.SGST = sgst;
    this.packageDetails.FinalAmount = final;
  }

  getPackageDetailsByID(id: number) {
    this.masterService.getPackageDetailsByID(id).subscribe((res: any) => {
      this.packageDetails = res.data;

      // Normalize CenterID to array if it's a single value
      if (this.packageDetails.CenterID && !Array.isArray(this.packageDetails.CenterID)) {
        this.packageDetails.CenterID = [this.packageDetails.CenterID];
      }

      // Normalize AreaIDs from AreaID if AreaIDs is missing
      if (!this.packageDetails.AreaIDs && this.packageDetails.AreaID) {
        this.packageDetails.AreaIDs = [this.packageDetails.AreaID];
      }

      if (this.packageDetails.StartTime) {
        const dob = new Date(this.packageDetails.StartTime);
        this.packageDetails.StartTime = dob.toLocaleDateString("en-CA"); // returns yyyy-MM-dd
      }
      this.enteredAmount = this.packageDetails.FinalAmount || 0;
      if (this.packageDetails.EndTime) {
        const dob = new Date(this.packageDetails.EndTime);
        this.packageDetails.EndTime = dob.toLocaleDateString("en-CA"); // returns yyyy-MM-dd
      }
      this.onAreaChange();
      if (this.packageDetails.MinimumAmount) {
        this.minAmountPercent = this.packageDetails.MinimumAmount;
        this.onMinAmountPercentChange();
      }
    });
  }

  getCompanyMaster() {
    this.masterService.getCompanyList().subscribe((res: any) => {
      this.companyList = res.data[0];
      this.SGST = this.companyList.SGST;
      this.CGST = this.companyList.CGST;
    });
  }

  getRegionList() {
    this.masterService.getRegionList().subscribe((res: any) => {
      this.regionList = res.Table;
    });
  }

  getAreaList() {
    this.masterService.getAreaList().subscribe((res: any) => {
      const areaID = sessionStorage.getItem("AllAreaIDs");
      const userType = sessionStorage.getItem("UserTypeID");
      if (userType == "1") {
        this.areaList = res.Table;
      } else {
        this.areaList = res.Table.filter((item: any) => areaID?.includes(item.AreaID.toString()));
      }
    });
  }

  getCenterList() {
    this.masterService.getCenterList().subscribe((res: any) => {
      this.allCenterList = res.data;
      this.onAreaChange();
    });
  }

  onAreaChange(isUserInteraction: boolean = false) {
    const areaIDs = this.packageDetails.AreaIDs || [];

    // 1. Filter the dropdown options
    if (areaIDs.length > 0) {
      this.centerList = this.allCenterList.filter((center: any) => areaIDs.includes(center.AreaID));
    } else {
      this.centerList = [];
    }

    if (isUserInteraction) {
      this.packageDetails.CenterID = this.centerList.map((c: any) => c.CenterID);
    } else {
      // 2. FIX: Remove selected centers that are no longer part of the selected areas
      // Only do this if we have loaded the centers (allCenterList check)
      if (this.allCenterList.length > 0 && this.packageDetails.CenterID && this.packageDetails.CenterID.length > 0) {
        this.packageDetails.CenterID = this.packageDetails.CenterID.filter((selectedCenterId: any) => {
          // Check if this selected center exists in the NEW filtered list
          return this.centerList.some((c: any) => c.CenterID === selectedCenterId);
        });
      }
    }
  }

  onClose() {
    this.dialogRef.close();
  }
}
